import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Enviar cookies em todos os pedidos axios automaticamente
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=unauthenticated

  // Interceptor global: redireciona silenciosamente para /login em qualquer 401
  useEffect(() => {
    const id = axios.interceptors.response.use(
      response => response,
      error => {
        const url = error.config?.url || '';
        const is401 = error.response?.status === 401;
        const isAuthRoute = url.includes('/auth/me') || url.includes('/auth/login');

        if (is401 && !isAuthRoute) {
          setUser(null);
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
            // Promise pendente para suprimir o erro enquanto a página navega
            return new Promise(() => {});
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Restaurar sessão via cookie httpOnly — API e axios são estáveis a nível de módulo
    // eslint-disable-next-line react-hooks/exhaustive-deps
    axios.get(`${API}/auth/me`)
      .then(r => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`, {}).catch(() => {});
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, login, logout, setUser }), [user, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() { return useContext(AuthContext); }

// Mantido por compatibilidade — autenticação feita via cookie httpOnly
export function getAuthHeader() {
  return {};
}

export { API };
